import { useState, useEffect, useCallback } from "react";
import { C, fmt } from "../styles/tokens";
import { depensesService, depenseCategoriesService } from "../api/finances";
import { reportsService } from "../api/services";
import { Card, Badge, Btn, Modal, Input, Select, Empty, Spinner } from "../components/ui";
import { handleApiError } from "../hooks/index";

const MODES_PAIEMENT = [
  { value: "ESPECES",      label: "Espèces" },
  { value: "CARTE",        label: "Carte bancaire" },
  { value: "MOBILE_MONEY", label: "Mobile Money" },
  { value: "VIREMENT",     label: "Virement" },
  { value: "AUTRE",        label: "Autre" },
];

const PERIODS = [
  { value: "month",  label: "Mois en cours" },
  { value: "7",      label: "7 derniers jours" },
  { value: "30",     label: "30 derniers jours" },
  { value: "90",     label: "3 derniers mois" },
  { value: "365",    label: "12 derniers mois" },
  { value: "custom", label: "Période personnalisée" },
];

// Premier jour du mois courant (YYYY-MM-DD) pour l'init du mode personnalisé
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

// Construit les params d'API à partir de l'état des filtres de période
const buildPeriodParams = (period, dateFrom, dateTo) => {
  if (period === "custom") {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }
  return { period };
};

/**
 * Dépenses : enregistrement direct (effet immédiat), catégorisation,
 * et résumé financier (recettes vs dépenses vs résultat).
 * Les dépenses liées à un achat de stock sont créées automatiquement
 * et ne sont pas modifiables ici.
 */
const DepensesScreen = ({ role, toast }) => {
  const roleLc = (role || "").toLowerCase();
  const isManager = ["manager", "admin", "administrateur"].includes(roleLc);
  const [depenses, setDepenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resume, setResume] = useState(null);
  const [fetching, setFetching] = useState(true);

  // Filtres
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(todayStr());
  const [catFilter, setCatFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  // Formulaire
  const [showForm, setShowForm] = useState(false);
  const [libelle, setLibelle] = useState("");
  const [montant, setMontant] = useState("");
  const [categorieId, setCategorieId] = useState("");
  const [modePaiement, setModePaiement] = useState("ESPECES");
  const [beneficiaire, setBeneficiaire] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    // En mode personnalisé, on attend au moins une borne
    if (period === "custom" && !dateFrom && !dateTo) return;
    setFetching(true);

    const periodParams = buildPeriodParams(period, dateFrom, dateTo);

    // Bornes pour le filtrage de la LISTE des dépenses
    let listParams = { categorie_id: catFilter };
    if (period === "custom") {
      if (dateFrom) listParams.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const end = new Date(dateTo); end.setHours(23, 59, 59, 999);
        listParams.date_to = end.toISOString();
      }
    } else if (period === "month") {
      listParams.date_from = new Date(firstOfMonth()).toISOString();
    } else {
      listParams.date_from = new Date(Date.now() - Number(period) * 86400000).toISOString();
    }

    Promise.all([
      depensesService.list(listParams)
        .then(d => setDepenses(Array.isArray(d) ? d : (d?.results ?? []))),
      depensesService.resume(periodParams).then(r => { if (r) setResume(r); }),
    ]).catch(() => {}).finally(() => setFetching(false));
  }, [period, dateFrom, dateTo, catFilter]);

  useEffect(() => { load(); }, [load]);

  const refreshResume = useCallback(() => {
    depensesService.resume(buildPeriodParams(period, dateFrom, dateTo))
      .then(r => { if (r) setResume(r); }).catch(() => {});
  }, [period, dateFrom, dateTo]);

  useEffect(() => {
    depenseCategoriesService.list()
      .then(d => {
        const cats = Array.isArray(d) ? d : (d?.results ?? []);
        // « Autre » toujours en dernier dans les listes
        cats.sort((a, b) =>
          (a.nom === "Autre") - (b.nom === "Autre") || a.nom.localeCompare(b.nom, "fr")
        );
        setCategories(cats);
      })
      .catch(() => {});
  }, []);

  const selectedCat = categories.find(c => String(c.id) === String(categorieId));
  const isAutre = selectedCat?.nom === "Autre";

  const resetForm = () => {
    setLibelle(""); setMontant(""); setCategorieId(""); setModePaiement("ESPECES");
    setBeneficiaire(""); setDescription(""); setShowForm(false);
  };

  const submit = async () => {
    if (!libelle || !montant || !categorieId) { toast.warning("", "Libellé, montant et catégorie requis"); return; }
    if (isAutre && !description.trim()) { toast.warning("", "Précisez la nature de la dépense pour la catégorie « Autre »"); return; }
    setLoading(true);
    try {
      const dep = await depensesService.create({
        libelle,
        montant: Number(montant),
        categorie_id: Number(categorieId),
        mode_paiement: modePaiement,
        beneficiaire,
        description,
      });
      setDepenses(p => [dep, ...p]);
      refreshResume();
      resetForm();
      toast.success("Dépense enregistrée", `${dep.libelle} — ${fmt(dep.montant)}`);
    } catch (err) { handleApiError(err, toast); } finally { setLoading(false); }
  };

  const remove = async (dep) => {
    if (!window.confirm(`Supprimer la dépense ${dep.id} — ${dep.libelle} ?`)) return;
    try {
      await depensesService.remove(dep.num_id);
      setDepenses(p => p.filter(d => d.num_id !== dep.num_id));
      refreshResume();
      toast.success("Dépense supprimée", "");
    } catch (err) { handleApiError(err, toast); }
  };

  const doExport = async (format = "excel") => {
    setExporting(true);
    try {
      // Bornes explicites pour que le fichier corresponde au filtre affiché.
      let periodExport;
      if (period === "custom") {
        periodExport = {};
        if (dateFrom) periodExport.date_from = dateFrom;
        if (dateTo) periodExport.date_to = dateTo;
      } else if (period === "month") {
        periodExport = { period: "month" };
      } else {
        periodExport = { period };
      }
      await depensesService.export({
        format,
        ...periodExport,
        ...(catFilter ? { categorie_id: catFilter } : {}),
      });
      toast.success("Export généré", "Le fichier a été téléchargé");
    } catch (err) {
      toast.warning("Export", err.message || "Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 28, overflowY: "auto", flex: 1 }}>
      {/* Répartition par catégorie */}
      {resume?.par_categorie?.length > 0 && (
        <Card style={{ padding: "16px 18px", marginBottom: 22 }}>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>
            Répartition par catégorie
          </div>
          {resume.par_categorie.map(c => {
            const pct = resume.depenses > 0 ? (c.total / resume.depenses) * 100 : 0;
            return (
              <div key={c.categorie_id} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: C.cream }}>{c.categorie} <span style={{ color: C.muted }}>({c.nombre})</span></span>
                  <span style={{ color: C.gold }}>{fmt(c.total)} · {pct.toFixed(0)}%</span>
                </div>
                <div style={{ height: 5, background: C.bg3, borderRadius: 3 }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: C.gold, borderRadius: 3, transition: "width .4s" }}/>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {/* Filtres + action */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap" }}>
        <Select label="Période" value={period} onChange={setPeriod} options={PERIODS} style={{ minWidth: 160 }}/>
        {period === "custom" && (
          <>
            <Input label="Début" type="date" value={dateFrom} onChange={setDateFrom} style={{ minWidth: 150 }}/>
            <Input label="Fin" type="date" value={dateTo} onChange={setDateTo} style={{ minWidth: 150 }}/>
          </>
        )}
        <Select label="Catégorie" value={catFilter} onChange={setCatFilter}
          options={[{ value: "", label: "Toutes les catégories" }, ...categories.map(c => ({ value: String(c.id), label: c.nom }))]}
          style={{ minWidth: 190 }}/>
        <div style={{ flex: 1 }}/>
        <Btn variant="ghost" loading={exporting} onClick={() => doExport("excel")}>⭳ Export Excel</Btn>
        {isManager && (
          <Btn onClick={() => setShowForm(true)}>+ Enregistrer une dépense</Btn>
        )}
      </div>

      {/* Liste */}
      {fetching ? <Spinner/> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {depenses.map((d, i) => (
            <Card key={d.num_id ?? d.id} className="anim-fadeUp" style={{ padding: "14px 18px", animationDelay: `${i * 30}ms` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.mutedL }}>{d.id}</span>
                    <Badge color={C.gold}>{d.categorie_nom}</Badge>
                    {d.achat_id && <Badge color={C.info}>Achat {d.achat_id}</Badge>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.cream }}>{d.libelle}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {d.beneficiaire && `${d.beneficiaire} · `}
                    {MODES_PAIEMENT.find(m => m.value === d.mode_paiement)?.label ?? d.mode_paiement}
                    {" · "}{d.cree_par} · {new Date(d.date_depense).toLocaleString("fr-FR")}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                  <span className="serif" style={{ fontSize: 18, fontWeight: 700, color: C.danger }}>−{fmt(d.montant)}</span>
                  {!d.achat_id && <Btn small variant="danger" onClick={() => remove(d)}>✕</Btn>}
                </div>
              </div>
            </Card>
          ))}
          {depenses.length === 0 && <Empty icon="💸" text="Aucune dépense sur la période"/>}
        </div>
      )}

      {/* Formulaire */}
      <Modal open={showForm} onClose={resetForm} title="Enregistrer une dépense" width={540}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="Libellé" value={libelle} onChange={setLibelle} placeholder="Ex : Facture d'électricité de juin" required/>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input label="Montant (FCFA)" type="number" min="0" step="1" value={montant} onChange={setMontant} placeholder="0" required/>
            <Select label="Catégorie" value={categorieId} onChange={setCategorieId} required
              options={[{ value: "", label: "— Choisir —" }, ...categories.map(c => ({ value: String(c.id), label: c.nom }))]}/>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select label="Mode de paiement" value={modePaiement} onChange={setModePaiement} options={MODES_PAIEMENT}/>
            <Input label="Bénéficiaire / Fournisseur" value={beneficiaire} onChange={setBeneficiaire} placeholder="Optionnel"/>
          </div>
          <Input
            label={isAutre ? "Nature de la dépense — précisez de quoi il s'agit" : "Description"}
            value={description} onChange={setDescription}
            placeholder={isAutre ? "Ex : Achat de décorations pour l'événement du samedi" : "Détails (optionnel)"}
            textarea required={isAutre}/>
          {isAutre && (
            <div style={{ fontSize: 11, color: C.gold, marginTop: -8 }}>
              ⚠ Catégorie « Autre » : la nature de la dépense est obligatoire.
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={resetForm}>Annuler</Btn>
            <Btn loading={loading} onClick={submit} disabled={!libelle || !montant || !categorieId || (isAutre && !description.trim())}>Enregistrer</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DepensesScreen;
